import { create } from 'zustand';
import httpClient from '../lib/http-client';

const ACCESS_TOKEN_KEY = 'serena.auth.accessToken';
const REFRESH_TOKEN_KEY = 'serena.auth.refreshToken';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
}

interface SessionResponse {
  accessToken: string;
  refreshToken: string;
  role: string;
  fullName: string;
  email: string;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  role: string | null;
  isLoading: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<{ error: Error | null }>;
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  /** Loads /auth/me into the store. Also fills in the user id, which the
   *  login response deliberately omits. */
  loadProfile: () => Promise<void>;
  /** Called by the http-client interceptor; false means unrecoverable. */
  refreshSession: () => Promise<boolean>;
  clearSession: () => void;
}

function readStored(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Safari private mode throws on localStorage access.
    return null;
  }
}

function writeStored(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

/**
 * Keeps the shape the old Supabase-backed store exposed (user, role, isAdmin,
 * isLoading, login, logout, initializeAuth) so ProtectedRoute, AdminOnly and
 * the login page need no changes.
 */
export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  role: null,
  isLoading: true,
  isAdmin: false,

  login: async (email: string, password: string) => {
    try {
      const { data } = await httpClient.post<SessionResponse>(
        '/auth/login',
        { email, password },
        { headers: { 'No-Auth': '1' } },
      );

      writeStored(ACCESS_TOKEN_KEY, data.accessToken);
      writeStored(REFRESH_TOKEN_KEY, data.refreshToken);

      set({
        user: { id: '', email: data.email, fullName: data.fullName },
        accessToken: data.accessToken,
        role: data.role,
        isAdmin: data.role === 'ADMIN',
        isLoading: false,
      });

      await get().loadProfile();
      return { error: null };
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } }).response?.data
          ?.message ?? 'No se pudo iniciar sesión';
      return { error: new Error(message) };
    }
  },

  logout: async () => {
    try {
      await httpClient.post('/auth/logout');
    } catch {
      // Server-side revocation is best-effort; the local session goes anyway.
    }
    get().clearSession();
  },

  clearSession: () => {
    writeStored(ACCESS_TOKEN_KEY, null);
    writeStored(REFRESH_TOKEN_KEY, null);
    set({
      user: null,
      accessToken: null,
      role: null,
      isAdmin: false,
      isLoading: false,
    });
  },

  loadProfile: async () => {
    try {
      const { data } = await httpClient.get<{
        id: string;
        email: string;
        fullName: string;
        role: string;
      }>('/auth/me');
      set({
        user: { id: data.id, email: data.email, fullName: data.fullName },
        role: data.role,
        isAdmin: data.role === 'ADMIN',
      });
    } catch {
      // The interceptor already attempted a refresh; reaching here means the
      // session is unrecoverable.
      get().clearSession();
    }
  },

  refreshSession: async () => {
    const refreshToken = readStored(REFRESH_TOKEN_KEY);
    if (!refreshToken) return false;

    try {
      const { data } = await httpClient.post<SessionResponse>(
        '/auth/refresh',
        { refreshToken },
        { headers: { 'No-Auth': '1' } },
      );

      writeStored(ACCESS_TOKEN_KEY, data.accessToken);
      writeStored(REFRESH_TOKEN_KEY, data.refreshToken);
      set({
        accessToken: data.accessToken,
        role: data.role,
        isAdmin: data.role === 'ADMIN',
      });
      return true;
    } catch {
      return false;
    }
  },

  initializeAuth: async () => {
    if (typeof window === 'undefined') return;

    const token = readStored(ACCESS_TOKEN_KEY);
    if (!token) {
      set({ isLoading: false });
      return;
    }

    set({ accessToken: token, isLoading: true });
    await get().loadProfile();
    set({ isLoading: false });
  },
}));
